<template>
  <base-modal
      :ref="modal.key"
      :show="open"
      maxWidth="600"
      :title="modalTitle"
      @on-confirm="saveItem()"
      @on-cancel="hide()"
      :okText="modalTitle"
      :closeText="$t('Close')"
  >
    <v-skeleton-loader
        :label="$t('User')"
        v-if="isLoading"
        type="list-item"
    ></v-skeleton-loader>

    <v-select
        v-if="!isLoading"
        :label="$t('User')"
        v-model="model.userId"
        :items="userList"
        item-text="text"
        item-value="id"
        @change="loadWidgets()"
        :error-messages="userErrors"
        @input="$v.model.userId.$touch()"
        @blur="$v.model.userId.$touch()"
    ></v-select>

    <v-skeleton-loader
        :label="$t('Target')"
        v-if="isLoading"
        type="list-item"
    ></v-skeleton-loader>

    <v-select
        v-if="isAdmin && !isLoading"
        :label="$t('Target')"
        v-model="model.target"
        :items="targetList"
        @change="loadValues()"
        item-text="text"
        item-value="id"
        :error-messages="targetErrors"
        @input="$v.model.target.$touch()"
        @blur="$v.model.target.$touch()"
    ></v-select>

    <v-select
        v-if="model.target > 1 && isAdmin && !isLoading"
        :label="$t('Value')"
        v-model="model.targetValue"
        :items="targetValueList"
        item-text="text"
        item-value="id"
        multiple
        :error-messages="targetValueErrors"
        @input="$v.model.targetValue.$touch()"
        @blur="$v.model.targetValue.$touch()"
    ></v-select>

    <v-select
        v-if="!isLoading"
        :label="$t('Widget')"
        v-model="model.widgetIds"
        :items="widgetList"
        item-text="title"
        item-value="id"
        multiple
        :error-messages="widgetIdsErrors"
        @input="$v.model.widgetIds.$touch()"
        @blur="$v.model.widgetIds.$touch()"
    ></v-select>

    <v-checkbox
        v-model="model.keepActualDashboard"
        :label="$t('KeepWidgets')"
        color="info"
        hide-details
    ></v-checkbox>

  </base-modal>
</template>

<script>
import { required, requiredIf } from 'vuelidate/lib/validators';
import { validationMixin } from 'vuelidate';
import Utils from '@/ecm/common/Utils';
import RoleService from '@/services/RoleService';
import UserService from '@/services/UserService';
import WidgetService from '@/services/WidgetService';
import UserWidgetService from '@/services/UserWidgetService';
import WidgetType from '@/ecm/variables/WidgetType';
import DashboardService from '@/services/DashboardService';

export default {
    data() {
        return {
            targetList: [
                {
                    id: '1',
                    text: this.$t('Organization'),
                },
                {
                    id: '2',
                    text: this.$t('Role'),
                },
                {
                    id: '3',
                    text: this.$t('User'),
                },
            ],
            targetValueList: [],
            userList: [],
            widgetList: [],
            model: {
                userId: null,
                target: null,
                targetValue: null,
                widgetIds: null,
                keepActualDashboard: true,
            },
            modal: {
                key: 'widgetImportModal',
            },
            open: false,
            isLoading: false
        };
    },
    mixins: [validationMixin],
    validations: {
        model: {
            userId: { required },
            widgetIds: { required },
            target: {
                required: requiredIf(function () {
                    return this.isAdmin;
                }),
            },
            targetValue: {
                required: requiredIf(function () {
                    return this.model.target != '1' && this.isAdmin;
                }),
            },
        },
    },
    computed: {
        isAdmin() {
            return (
                this.$store.getters['account/user'] &&
                this.$store.getters['account/user'].SysLevel == 2
            );
        },
        modalTitle() {
            return this.isAdmin ? this.$t('Export') : this.$t('Import');
        },
        modalInstance() {
            return this.$refs[this.modal.key];
        },
        targetErrors() {
            const errors = [];
            if (!this.$v.model.target.$dirty) return errors;
            !this.$v.model.target.required && errors.push(this.$t('RequiredField'));
            return errors;
        },
        userErrors() {
            const errors = [];
            if (!this.$v.model.userId.$dirty) return errors;
            !this.$v.model.userId.required && errors.push(this.$t('RequiredField'));
            return errors;
        },
        widgetIdsErrors() {
            const errors = [];
            if (!this.$v.model.widgetIds.$dirty) return errors;
            !this.$v.model.widgetIds.required &&
                errors.push(this.$t('RequiredField'));
            return errors;
        },
        targetValueErrors() {
            const errors = [];
            if (!this.$v.model.targetValue.$dirty) return errors;
            !this.$v.model.targetValue.required &&
                errors.push(this.$t('RequiredField'));
            return errors;
        },
    },
    methods: {
        loadValues() {
            this.$v.$reset();
            this.targetValueList = [];
            this.model.targetValue = '';
            if (this.model.target == '2') {
                RoleService.getList(true).then((result) => {
                    this.targetValueList = result;
                });
            }
            if (this.model.target == '3') {
                UserService.getList().then((result) => {
                    this.targetValueList = result;
                });
            }
        },
        loadWidgets() {
            this.loading = true;
            UserWidgetService.getForUserId(this.model.userId).then((data) => {
                this.widgetList = data.map((w) => {
                    return {
                        id: w.Id,
                        title: w.Settings.WidgetTitle || this.getWidgetName(w.WidgetID),
                    };
                });
                this.model.widgetIds = this.widgetList.map((w) => w.id);
                this.loading = false;
            });
        },
        getWidgetName(widgetId) {
            switch (widgetId) {
            case WidgetType.Baskets:
                return this.$t('Baskets');
            case WidgetType.UserSearches:
                return this.$t('SavedSearches');
            case WidgetType.Distributions:
                return this.$t('DistributionInternalExternal');
            case WidgetType.UploadedDocumentsChart:
                return this.$t('WidgetLoadedDocumentTitle');
            case WidgetType.DocumentsByContentTypeChart:
                return this.$t('WidgetDocumentsByContentTypeTitle');
            case WidgetType.DocumentsByWorkflowChart:
                return this.$t('WidgetDocumentsByWorkflowTitle');
            case WidgetType.Bookmark:
                return this.$t('WidgetBookmarkTitle');
            case WidgetType.Note:
                return this.$t('WidgetNoteTitle');
            case WidgetType.Card:
                return this.$t('WidgetCardTitle');
            case WidgetType.SearchHistory:
                return this.$t('WidgetSearchHistoryTitle');
            case WidgetType.Newsfeed:
                return this.$t('WidgetNewsfeedTitle');
            case WidgetType.DocumentTracker:
                return this.$t('WidgetDocumentTrackerTitle');
            case WidgetType.AutorecognitionTracker:
                return this.$t('WidgetAutorecognitionTrackerTitle');
            }
        },
        saveItem() {
            this.$v.$touch();
            if (this.$v.$invalid) {
                return;
            }

            this.modalInstance.progress(true);

            var importDto = {
                UserId: parseInt(this.model.userId),
                Target: this.isAdmin ? parseInt(this.model.target) : 3,
                TargetValue: this.isAdmin
                    ? Array.isArray(this.model.targetValue) ? this.model.targetValue : []
                    : [this.$store.getters['account/user'].Id],
                WidgetIds: this.model.widgetIds,
                KeepActualDashboard: this.model.keepActualDashboard,
            };

            WidgetService.importLayout(importDto)
                .then((result) => {
                    Utils.toast.success(this.$t('DashboardSaved'));
                    this.$emit('on-saved', importDto);
                    if (result.hasBeenImported) {
                        Utils.toast.info(this.$t('ChangesNextLoginToOtherUser').replace('{0}', result.login));
                    } else {
                        Utils.toast.info(this.$t('Site.Widgets.WidgetImport.NoNewSearchesImported').replace('{0}', result.login));
                    }
                })
                .finally(() => {
                    this.hide();
                    this.modalInstance.progress(false);
                });
        },
        show() {
            if (this.userList.length === 0) {
                this.loading = true;
                DashboardService.getPublicDashboardUsers().then((result) => {
                    this.userList = result;
                    if (this.isAdmin) {
                        this.model.userId = this.$store.getters['account/user'].Id + '';
                        this.loadWidgets();
                    } else {
                        this.loading = false;
                    }

                    this.open = true;
                    this.$v.$reset();
                });
            } else {
                this.open = true;
                this.$v.$reset();
            }
        },
        hide() {
            this.open = false;
            this.$v.$reset();
        },
    },
};
</script>
